import React, { useEffect, useState, useRef, createElement } from 'react';
import { PlusIcon, SearchIcon, Loader2Icon, ImageIcon, CopyIcon, PencilIcon, Trash2Icon, XIcon, CheckIcon, ImageOffIcon, LinkIcon, UploadIcon } from 'lucide-react';
import { mediaLibraryApi, MediaLibraryRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
export function MediaLibrary() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const region = currentSubRegion || '';
  const [images, setImages] = useState<MediaLibraryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingImage, setEditingImage] = useState<MediaLibraryRecord | null>(null);
  const [imageToDelete, setImageToDelete] = useState<MediaLibraryRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    Image_Name: '',
    Image_URL: '',
    Description: '',
    Category: ''
  });
  // Upload mode: 'upload' or 'url'
  const [inputMode, setInputMode] = useState<'upload' | 'url'>('upload');
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFileSize, setUploadFileSize] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  // Track broken images
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());
  // Load images
  useEffect(() => {
    loadImages();
  }, [team, region]);
  const loadImages = async () => {
    setIsLoading(true);
    try {
      const all = await mediaLibraryApi.list();
      // Filter by team AND region
      const teamFiltered = all.filter(img => (img.Team === team || img.Team === currentWorkspace.name) && (!region || img.Region === region || !img.Region));
      setImages(teamFiltered);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to load images', {
        description: msg
      });
    } finally {
      setIsLoading(false);
    }
  };
  // Get unique categories for filter dropdown
  const categories = Array.from(new Set(images.map(img => img.Category).filter(Boolean))).sort();
  // Filter images
  const filteredImages = images.filter(img => {
    const matchesSearch = !searchQuery || img.Image_Name.toLowerCase().includes(searchQuery.toLowerCase()) || (img.Description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || img.Category === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  // Copy text to clipboard with fallback
  const copyToClipboard = async (text: string): Promise<boolean> => {
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Falls through to execCommand fallback
      }
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      textarea.remove();
      return ok;
    } catch {
      return false;
    }
  };
  // Copy URL to clipboard
  const handleCopyUrl = async (url: string, id: string, _imageName: string) => {
    const copied = await copyToClipboard(url);
    if (copied) {
      setCopiedId(id);
      toast.success('URL copied to clipboard', {
        description: 'Paste it into any HTML as an image src.'
      });
      setTimeout(() => setCopiedId(null), 2000);
    } else {
      toast.error('Failed to copy URL');
    }
  };
  // Open add modal
  const handleAdd = () => {
    setEditingImage(null);
    setFormData({
      Image_Name: '',
      Image_URL: '',
      Description: '',
      Category: ''
    });
    setInputMode('upload');
    setUploadFileName('');
    setUploadFileSize(0);
    setShowModal(true);
  };
  // Open edit modal
  const handleEdit = (img: MediaLibraryRecord) => {
    setEditingImage(img);
    setFormData({
      Image_Name: img.Image_Name || '',
      Image_URL: img.Image_URL || '',
      Description: img.Description || '',
      Category: img.Category || ''
    });
    // If existing URL is a data URL, show upload mode; otherwise URL mode
    setInputMode(img.Image_URL?.startsWith('data:') ? 'upload' : 'url');
    setUploadFileName('');
    setUploadFileSize(0);
    setShowModal(true);
  };
  // Process uploaded file → attempt to host it, fall back to base64 data URL
  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, GIF, WebP, SVG)');
      return;
    }
    // Warn if file is very large (> 1MB)
    if (file.size > 1024 * 1024) {
      toast.warning('Large image detected', {
        description: `${formatFileSize(file.size)} — consider using a smaller image or compressing it for better performance.`,
        duration: 5000
      });
    }
    const reader = new FileReader();
    reader.onload = async ev => {
      const dataUrl = ev.target?.result as string;
      setUploadFileName(file.name);
      setUploadFileSize(file.size);
      // Auto-fill name from filename if empty
      const nameWithoutExt = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      setFormData(prev => ({
        ...prev,
        Image_URL: dataUrl,
        ...(prev.Image_Name.trim() ? {} : {
          Image_Name: nameWithoutExt
        })
      }));
      toast.success('Image loaded — ready to save');
    };
    reader.readAsDataURL(file);
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };
  const handleModalDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) setIsDragOver(true);
  };
  const handleModalDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };
  const handleModalDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleModalDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setInputMode('upload');
      processFile(file);
    }
  };
  // Save (create or update)
  const handleSave = async () => {
    if (!formData.Image_Name.trim()) {
      toast.error('Image name is required');
      return;
    }
    if (!formData.Image_URL.trim()) {
      toast.error('Image URL is required');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        Image_Name: formData.Image_Name.trim(),
        Image_URL: formData.Image_URL.trim(),
        Description: formData.Description.trim(),
        Category: formData.Category.trim(),
        Team: team,
        Region: region
      };
      if (editingImage) {
        await mediaLibraryApi.update(editingImage.ID, payload);
        toast.success('Image updated', {
          description: `"${payload.Image_Name}" has been updated.`
        });
      } else {
        await mediaLibraryApi.create(payload);
        toast.success('Image added', {
          description: `"${payload.Image_Name}" has been added to the library.`
        });
      }
      setShowModal(false);
      loadImages();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(editingImage ? 'Failed to update image' : 'Failed to add image', {
        description: msg
      });
    } finally {
      setIsSaving(false);
    }
  };
  // Delete
  const handleDelete = (img: MediaLibraryRecord) => {
    setImageToDelete(img);
  };
  const confirmDelete = async () => {
    if (!imageToDelete) return;
    try {
      await mediaLibraryApi.delete(imageToDelete.ID);
      toast.success('Image deleted', {
        description: `"${imageToDelete.Image_Name}" has been removed.`
      });
      loadImages();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete image', {
        description: msg
      });
    }
    setImageToDelete(null);
  };
  // Handle image load error
  const handleImageError = (id: string) => {
    setBrokenImages(prev => new Set(prev).add(id));
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-5163" data-mp="1r63pk7okybn5">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-5164" data-mp="1mc97t9sjc6qp" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-5165" data-mp="1hdxwsvqi7q6w">
          Loading media library...
        </span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-5166" data-mp="1ddx90khim2n6">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-5167" data-mp="18dj9jqtzc10r">
        <div data-id="element-5168" data-mp="18czv4k3jfurh">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5169" data-mp="18flys8nb5q4f">
            Media Library
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-5170" data-mp="13gj2bcle8s82">
            Upload and manage images for forms, emails, and landing pages
          </p>
        </div>
        <button onClick={handleAdd} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors" data-id="element-5171" data-mp="2jhtl7jtd9iap">
          <PlusIcon className="h-4 w-4" data-id="element-5172" data-mp="2schmyzzqghur" />
          Add Image
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5173" data-mp="24lvs6aeqou2p">
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-5174" data-mp="20lbw7a5wjwi5">
          <div className="flex-1 relative" data-id="element-5175" data-mp="2ejia75rc51qw">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5176" data-mp="2ekkz5853ku46" />
            <input type="text" placeholder="Search by name or description..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-5177" data-mp="29jx7qlcyib9f" />
          </div>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 sm:w-48" data-id="element-5178" data-mp="1brp8k4firguu">
            <option value="" data-id="element-5179" data-mp="12sloax4m8ver">All Categories</option>
            {categories.map(cat => <option key={cat} value={cat} data-id="element-5180" data-mp="1qgvfyfoigddt">
                {cat}
              </option>)}
          </select>
        </div>
        <p className="text-xs text-gray-500 mt-2" data-id="element-5181" data-mp="1gqin806jldwl">
          Showing {filteredImages.length} of {images.length} images
        </p>
      </div>

      {/* Empty State */}
      {images.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-5182" data-mp="29f7s8aapy3dm">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-5183" data-mp="2e5o4hr2sziye">
            <ImageIcon className="h-8 w-8 text-gray-400" data-id="element-5184" data-mp="2o1d72nnxre2a" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-5185" data-mp="2nzk7fsbqpu3s">
            No images yet
          </h3>
          <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto" data-id="element-5186" data-mp="2j0pie8n6j9b8">
            Add images to your media library to use them in forms, emails, and
            landing pages.
          </p>
          <button onClick={handleAdd} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors" data-id="element-5187" data-mp="296gpvky5fgd2">
            <PlusIcon className="h-4 w-4" data-id="element-5188" data-mp="1vcz8a5p0wdpo" />
            Add Your First Image
          </button>
        </div> : filteredImages.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-5189" data-mp="1qf7e150wofib">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-5190" data-mp="1ldqi7nxa3j8l">
            <SearchIcon className="h-8 w-8 text-gray-400" data-id="element-5191" data-mp="1hehlqbaut79c" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-5192" data-mp="3t1bedzi9tuvg">
            No matches found
          </h3>
          <p className="text-sm text-gray-500" data-id="element-5193" data-mp="vpqlnch88ep">
            Try adjusting your search or filter criteria.
          </p>
        </div> /* Image Grid */ : <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-5194" data-mp="aux0g0k05kx2">
          {filteredImages.map(img => <div key={img.ID} className="bg-white border border-gray-200 rounded-lg overflow-hidden group" data-id="element-5195" data-mp="39dlsua0xgquo">
              {/* Thumbnail */}
              <div className="relative h-40 bg-gray-100" data-id="element-5196" data-mp="3ja8doyt97hwp">
                {brokenImages.has(img.ID) ? <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400" data-id="element-5197" data-mp="3o1qe9lrd7gh5">
                    <ImageOffIcon className="h-8 w-8 mb-1" data-id="element-5198" data-mp="2ql1j2awx7cgq" />
                    <span className="text-xs" data-id="element-5199" data-mp="2vl5w1qymdp27">Failed to load</span>
                  </div> : <img src={img.Image_URL} alt={img.Image_Name} className="w-full h-full object-cover" onError={() => handleImageError(img.ID)} data-id="element-5200" data-mp="114fo421kl1u6" />}
                {/* Hover overlay with actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2" data-id="element-5201" data-mp="163s8mg4yxwtl">
                  <button onClick={() => handleCopyUrl(img.Image_URL, img.ID, img.Image_Name)} className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors" title="Copy URL" data-id="element-5202" data-mp="1kurg1nu2ifbl">
                    {copiedId === img.ID ? <CheckIcon className="h-4 w-4 text-green-600" data-id="element-5203" data-mp="3vlht928fb5qs" /> : <CopyIcon className="h-4 w-4" data-id="element-5204" data-mp="3qnfznnddfo12" />}
                  </button>
                  <button onClick={() => handleEdit(img)} className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors" title="Edit" data-id="element-5205" data-mp="264dxrkjdnuqi">
                    <PencilIcon className="h-4 w-4" data-id="element-5206" data-mp="21d49oodh8zgq" />
                  </button>
                  <button onClick={() => handleDelete(img)} className="p-2 bg-white rounded-full text-red-600 hover:bg-red-50 transition-colors" title="Delete" data-id="element-5207" data-mp="21b17l5z1vfvn">
                    <Trash2Icon className="h-4 w-4" data-id="element-5208" data-mp="1ed6byk0o5mv4" />
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="p-3" data-id="element-5209" data-mp="1iejk50r8i257">
                <h3 className="font-medium text-gray-900 text-sm truncate" title={img.Image_Name} data-id="element-5210" data-mp="38qyypf7x78l2">
                  {img.Image_Name}
                </h3>
                {img.Category && <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full" data-id="element-5211" data-mp="pkmvyxvtvoaf">
                    {img.Category}
                  </span>}
                {img.Description && <p className="text-xs text-gray-500 mt-1.5 line-clamp-2" title={img.Description} data-id="element-5212" data-mp="gl02cy9cbmrx">
                    {img.Description}
                  </p>}
              </div>
            </div>)}
        </div>}

      {/* Add/Edit Modal */}
      {showModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5213" data-mp="zgq5iot0ep7e">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5214" data-mp="uj6espf4kjyk">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => !isSaving && setShowModal(false)} data-id="element-5215" data-mp="uh3r4c1g32x0" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg" onDragEnter={handleModalDragEnter} onDragLeave={handleModalDragLeave} onDragOver={handleModalDragOver} onDrop={handleModalDrop} data-id="element-5216" data-mp="1n6aoh7p5xmgo">
              {/* Drag overlay */}
              {isDragOver && <div className="absolute inset-0 z-10 bg-blue-50/95 border-2 border-dashed border-blue-400 rounded-lg flex flex-col items-center justify-center gap-2 pointer-events-none" data-id="element-5217" data-mp="28irooqdxecmd">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-5218" data-mp="2ig5y461fm156">
                    <UploadIcon className="h-6 w-6 text-blue-600" data-id="element-5219" data-mp="2n75sp4n2w74i" />
                  </div>
                  <p className="text-sm font-medium text-blue-700" data-id="element-5220" data-mp="2s70h91jdskme">
                    Drop image here
                  </p>
                </div>}

              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-5221" data-mp="316nm5xbbk8k7">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-5222" data-mp="3mhm1hjiougv6">
                  {editingImage ? 'Edit Image' : 'Add Image'}
                </h3>
                <button onClick={() => !isSaving && setShowModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-5223" data-mp="3hl55y495t082">
                  <XIcon className="h-5 w-5" data-id="element-5224" data-mp="3rijkfybivexq" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-4 space-y-4" data-id="element-5225" data-mp="33pjku861r0kx">
                {/* Preview */}
                {formData.Image_URL && <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50" data-id="element-5226" data-mp="3dh75nq83ljdf">
                    <img src={formData.Image_URL} alt="Preview" className="w-full h-32 object-contain" onError={e => {
                ;
                (e.target as HTMLImageElement).style.display = 'none';
              }} data-id="element-5227" data-mp="38r0jry4z0cyv" />
                  </div>}

                {/* Image Name */}
                <div data-id="element-5228" data-mp="10b97cy43puwh">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5229" data-mp="10bsnc9a6y97v">
                    Image Name <span className="text-red-500" data-id="element-5230" data-mp="lmw8ntdmpxya">*</span>
                  </label>
                  <input type="text" value={formData.Image_Name} onChange={e => setFormData({
                ...formData,
                Image_Name: e.target.value
              })} placeholder="e.g., Hero Banner, Logo, Team Photo" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-5231" data-mp="qkejxqf4cp9g" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-5232" data-mp="3pyhza7prnnfj">
                    Use this name to reference the image in AI prompts
                  </p>
                </div>

                {/* Input Mode Toggle */}
                <div data-id="element-5233" data-mp="2w8to0kxi3pgm">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5234" data-mp="30cjexh1nej4t">
                    Image Source <span className="text-red-500" data-id="element-5235" data-mp="2mcqlvf2nfqto">*</span>
                  </label>
                  <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5 mb-3" data-id="element-5236" data-mp="2mf4gxnxbx318">
                    <button type="button" onClick={() => setInputMode('upload')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${inputMode === 'upload' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-5237" data-mp="2rdo6rzf1myyv">
                      Upload File
                    </button>
                    <button type="button" onClick={() => setInputMode('url')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${inputMode === 'url' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-5238" data-mp="28izdia0nmczj">
                      Paste URL
                    </button>
                  </div>

                  {inputMode === 'upload' ? <div data-id="element-5239" data-mp="1p0d0saqfr9kl">
                      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-id="element-5240" data-mp="1oxh0wgv12ati" />
                      {uploadFileName && formData.Image_URL ? <div className="flex items-center gap-3 p-3 rounded-lg border bg-green-50 border-green-200" data-id="element-5241" data-mp="27prz3xeqlkla">
                          <div className="w-10 h-10 rounded bg-white overflow-hidden flex-shrink-0 border border-green-200" data-id="element-5242" data-mp="22qouge2o5gdu">
                            <img src={formData.Image_URL} alt="" className="w-full h-full object-cover" data-id="element-5243" data-mp="2hoqy0o9pa1v3" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5244" data-mp="2or5u6st5jwpu">
                            <p className="text-sm font-medium truncate text-green-800" data-id="element-5245" data-mp="2orp7fw7uxwxj">
                              {uploadFileName}
                            </p>
                            <p className="text-xs text-green-600" data-id="element-5246" data-mp="2yia95sitvhko">
                              {formatFileSize(uploadFileSize)} · Ready to save
                            </p>
                          </div>
                          <button type="button" onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      Image_URL: ''
                    }));
                    setUploadFileName('');
                    setUploadFileSize(0);
                  }} className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors" data-id="element-5247" data-mp="37glx1msb819f">
                            <XIcon className="h-4 w-4" data-id="element-5248" data-mp="1arav36h4s0xe" />
                          </button>
                        </div> : <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full flex flex-col items-center gap-2 p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer" data-id="element-5249" data-mp="15t0yqsjo6ua7">
                          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-5250" data-mp="1ohxtexizbosy">
                            <UploadIcon className="h-5 w-5 text-gray-500" data-id="element-5251" data-mp="1jjmi0id1d68m" />
                          </div>
                          <div className="text-center" data-id="element-5252" data-mp="d5dh9mdp4jrs">
                            <p className="text-sm font-medium text-gray-700" data-id="element-5253" data-mp="84psz8guqh19">
                              Click to select or drag & drop
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5" data-id="element-5254" data-mp="21pwoh4j5ja0z">
                              PNG, JPG, GIF, WebP, SVG
                            </p>
                          </div>
                        </button>}
                      {uploadFileSize > 500 * 1024 && <p className="text-xs text-amber-600 mt-1.5" data-id="element-5255" data-mp="2fpo8pn09hmnf">
                          ⚠ Large images may slow down forms and emails.
                          Consider compressing or using a smaller version.
                        </p>}
                    </div> : <div data-id="element-5256" data-mp="2aq1v3cgiu4e3">
                      <div className="relative" data-id="element-5257" data-mp="2ap9z40j0i4na">
                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5258" data-mp="ucbh9dzlo1r9" />
                        <input type="url" value={formData.Image_URL.startsWith('data:') ? '' : formData.Image_URL} onChange={e => {
                    setFormData({
                      ...formData,
                      Image_URL: e.target.value
                    });
                    setUploadFileName('');
                    setUploadFileSize(0);
                  }} placeholder="https://example.com/image.jpg" className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-5259" data-mp="zb6azaz3vaq5" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-5260" data-mp="2b4gotjl7kd5m">
                        Paste a publicly accessible image URL
                      </p>
                    </div>}
                </div>

                {/* Category */}
                <div data-id="element-5261" data-mp="a1xvhhyqvdvv">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5262" data-mp="a0mtnnov0g7e">
                    Category
                  </label>
                  <input type="text" list="category-suggestions" value={formData.Category} onChange={e => setFormData({
                ...formData,
                Category: e.target.value
              })} placeholder="e.g., hero, logo, banner, icon" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-5263" data-mp="js0npfy1knt5" />
                  <datalist id="category-suggestions" data-id="element-5264" data-mp="1cgy5lhyccild">
                    {categories.map(cat => <option key={cat} value={cat} data-id="element-5265" data-mp="1hfsm2d8u5v2b" />)}
                  </datalist>
                </div>

                {/* Description */}
                <div data-id="element-5266" data-mp="31xa4bli1zpts">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5267" data-mp="2wz0apfbz8xaa">
                    Description
                  </label>
                  <textarea value={formData.Description} onChange={e => setFormData({
                ...formData,
                Description: e.target.value
              })} placeholder="Optional description or notes about this image" rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-5268" data-mp="2s78il0d6aji1" />
                </div>

                {/* Team / Region */}
                <div className="grid grid-cols-2 gap-4" data-id="element-5269" data-mp="7gq74f785okx">
                  <div data-id="element-5270" data-mp="2j7v2fxwut8p">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5271" data-mp="2iokjkalb74p">
                      Team
                    </label>
                    <input type="text" value={team} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-500" data-id="element-5272" data-mp="37jrv0zfmrtnm" />
                  </div>
                  <div data-id="element-5273" data-mp="32qgokzeqdew8">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5274" data-mp="32px69hl64oho">
                      Region
                    </label>
                    <input type="text" value={region || '(All regions)'} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-500" data-id="element-5275" data-mp="2trv777kzw5x1" />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-5276" data-mp="i2kic787ane6">
                <button onClick={() => setShowModal(false)} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-5277" data-mp="22l4ttu27qwoq">
                  Cancel
                </button>
                <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-5278" data-mp="27h3aswl7pcf4">
                  {isSaving && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-5279" data-mp="2v7z8ubg9h3ut" />}
                  {isSaving ? 'Saving...' : editingImage ? 'Update' : 'Add Image'}
                </button>
              </div>
            </div>
          </div>
        </div>}

      <ConfirmModal isOpen={!!imageToDelete} title="Delete Image" message={`Delete "${imageToDelete?.Image_Name}"? This cannot be undone.`} confirmLabel="Delete" variant="danger" onCancel={() => setImageToDelete(null)} onConfirm={confirmDelete} data-id="element-5280" data-mp="4tlm2eg1o9h0" />
    </div>;
}